import React from 'react';

import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils';

import PageTransition from './page-transition';

export type Tab = {
  id: string;
  label: string;
  content: React.ReactNode;
  noPadding?: boolean;
};

export type TabsProps = {
  tabs: Tab[];
  defaultTab?: string;
  className?: string;
  ariaLabel?: string;
  noBorder?: boolean;
  onTabChange?: (tabId: string) => void;
  tabHrefMap?: Partial<Record<string, string>>;
  /** When true, the tabs container and active panel participate in flex height propagation so content can fill remaining space. */
  fillHeight?: boolean;
};

function Tabs({
  tabs,
  defaultTab,
  className,
  ariaLabel = 'Tabs',
  noBorder = false,
  onTabChange,
  tabHrefMap,
  fillHeight = false,
}: Readonly<TabsProps>) {
  const { pathname } = useLocation();

  const segments = pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? '';
  const matchedTab = tabs.find((tab) => tab.id === lastSegment);
  const activeTab = matchedTab?.id ?? defaultTab ?? tabs[0]?.id ?? '';

  const basePath = matchedTab
    ? '/' + segments.slice(0, -1).join('/')
    : pathname;

  const toHref = (tabId: string) => {
    const overrideHref = tabHrefMap?.[tabId];
    if (overrideHref) return overrideHref;
    const base = basePath === '/' ? '' : basePath;
    return `${base}/${tabId}`;
  };

  return (
    <div
      className={cn(className, fillHeight && 'flex min-h-0 flex-1 flex-col')}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={cn(
          'flex shrink-0',
          !noBorder && 'border-border/50 border-b-2'
        )}
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.id}
            to={toHref(tab.id)}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            onClick={() => onTabChange?.(tab.id)}
            className={cn(
              'focus-visible:ring-ring hover:text-foreground hover:bg-muted/50 relative cursor-pointer px-3 py-1.5 text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:rounded-t-md focus-visible:ring-2 focus-visible:ring-inset',
              activeTab === tab.id
                ? 'text-primary hover:text-primary after:bg-primary after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5 after:rounded-t after:transition-all after:duration-200'
                : 'text-muted-foreground'
            )}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`panel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
          hidden={activeTab !== tab.id}
          className={cn(
            fillHeight && activeTab === tab.id && 'flex min-h-0 flex-1 flex-col'
          )}
        >
          {activeTab === tab.id && (
            <PageTransition
              type="fade"
              duration={150}
              className={cn(fillHeight && 'flex min-h-0 flex-1 flex-col')}
            >
              <div
                className={cn(
                  !tab.noPadding && 'pt-3',
                  fillHeight && 'flex min-h-0 flex-1 flex-col'
                )}
              >
                {tab.content}
              </div>
            </PageTransition>
          )}
        </div>
      ))}
    </div>
  );
}

export { Tabs };
export default Tabs;
